import React, { useEffect, useState } from "react";
import { api, API, apiErr } from "@/lib/api";
import { waLink } from "@/lib/wa";
import { toast } from "sonner";
import { X, Printer, Download, Loader2 } from "lucide-react";

let WA_NUMBER = "";
const scanUrl = (code) => waLink(WA_NUMBER, code);
const qrSrc = (code, box = 6) => `${API}/qr?data=${encodeURIComponent(scanUrl(code))}&box=${box}`;

export default function BatchQR({ batch, onClose }) {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/public/whatsapp-number"), api.get(`/coupon-batches/${batch.id}/export`)])
      .then(([n, r]) => { WA_NUMBER = n.data.number || ""; setCoupons(r.data.coupons || []); })
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, [batch.id]);

  const shown = coupons.slice(0, 300);

  const downloadOne = async (code) => {
    try {
      const res = await api.get(`/qr`, { params: { data: scanUrl(code), box: 10 }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${code}.png`; a.click();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const printSheet = () => {
    const w = window.open("", "_blank");
    const cards = shown.map((c) => `
      <div style="display:inline-flex;flex-direction:column;align-items:center;width:150px;margin:10px;padding:10px;border:1px solid #eee;border-radius:8px;page-break-inside:avoid;">
        <img src="${qrSrc(c.code, 6)}" width="120" height="120" />
        <div style="font-family:monospace;font-size:11px;margin-top:6px;">${c.code}</div>
        <div style="font-size:10px;color:#8B0000;font-weight:700;">${c.reward_points} pts</div>
      </div>`).join("");
    w.document.write(`<html><head><title>${batch.name} — QR Codes</title></head>
      <body style="font-family:Inter,sans-serif;padding:16px;">
      <h2 style="color:#8B0000;">Jewell Rewards — ${batch.name}</h2>
      <p style="color:#555;">${batch.product_name} · ${shown.length} coupons</p>
      <div style="display:flex;flex-wrap:wrap;">${cards}</div>
      <script>setTimeout(()=>window.print(),600);</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="batch-qr-modal">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-display font-bold text-gray-900">QR Codes — {batch.name}</h3>
            <p className="text-xs text-gray-500">{batch.product_name} · {batch.generated} coupons · each QR opens WhatsApp with the coupon code typed in</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="qr-print-btn" onClick={printSheet} className="flex items-center gap-2 text-sm font-semibold bg-[#8B0000] text-white px-3 py-2 rounded-lg hover:bg-[#6B0000]"><Printer className="h-4 w-4" /> Print Sheet</button>
            <button data-testid="qr-close-btn" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="p-5 overflow-y-auto scrollbar-thin">
          {loading ? <div className="py-16 text-center"><Loader2 className="h-8 w-8 animate-spin text-[#8B0000] mx-auto" /></div> : (
            <>
              {coupons.length > 300 && <p className="text-xs text-amber-600 mb-3">Showing first 300 of {coupons.length}. Use Print Sheet for the full batch.</p>}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {shown.map((c) => (
                  <div key={c.code} className="border border-gray-200 rounded-xl p-3 flex flex-col items-center" data-testid={`qr-item-${c.code}`}>
                    <img src={qrSrc(c.code, 6)} alt={c.code} className="h-28 w-28" loading="lazy" />
                    <div className="mt-2 font-mono text-[11px] text-gray-700 break-all text-center">{c.code}</div>
                    <div className="text-[11px] font-bold text-[#8B0000]">{c.reward_points} pts</div>
                    <button onClick={() => downloadOne(c.code)} data-testid={`qr-download-${c.code}`}
                      className="mt-2 flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-[#8B0000]"><Download className="h-3.5 w-3.5" /> PNG</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
